import { gql, useMutation, useQuery } from '@apollo/client';
import React, { useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { PageHeader } from '../../components/PageHeader';

type BillingPeriod = 'MONTHLY' | 'YEARLY';
type SubscriptionStatus =
  | 'PENDING'
  | 'ACTIVE'
  | 'GRACE_PERIOD'
  | 'PAUSED'
  | 'CANCELED'
  | 'EXPIRED'
  | 'REVOKED';

type AdminSubscriptionRow = {
  user: {
    id: string;
    email: string;
    fullName: string;
    coach: boolean;
  };
  subscription: {
    id: number;
    provider: string;
    environment: string;
    productId: string;
    billingPeriod: BillingPeriod;
    status: SubscriptionStatus;
    verificationStatus: string;
    autoRenewing: boolean;
    currentPeriodStart?: string;
    currentPeriodEnd?: string;
  };
  entitlement: {
    status: string;
    isActive: boolean;
    validUntil?: string;
    subscription?: {
      id: number;
    };
  };
};

type AdminSubscriptionsData = {
  adminCoachMembershipSubscriptions: AdminSubscriptionRow[];
};

type AdminSubscriptionsVariables = {
  query?: string;
  status?: SubscriptionStatus;
  limit?: number;
};

type MutationResultData = {
  subscription: {
    id: number;
  };
  entitlement: {
    isActive: boolean;
  };
};

const ADMIN_SUBSCRIPTIONS_QUERY = gql`
  query AdminCoachMembershipSubscriptions(
    $query: String
    $status: SubscriptionStatus
    $limit: Int
  ) {
    adminCoachMembershipSubscriptions(
      query: $query
      status: $status
      limit: $limit
    ) {
      user {
        id
        email
        fullName
        coach
      }
      subscription {
        id
        provider
        environment
        productId
        billingPeriod
        status
        verificationStatus
        autoRenewing
        currentPeriodStart
        currentPeriodEnd
      }
      entitlement {
        status
        isActive
        validUntil
        subscription {
          id
        }
      }
    }
  }
`;

const ADMIN_GRANT_MEMBERSHIP_MUTATION = gql`
  mutation AdminGrantCoachMembership($input: AdminGrantCoachMembershipInput!) {
    adminGrantCoachMembership(input: $input) {
      subscription {
        id
      }
      entitlement {
        isActive
      }
    }
  }
`;

const ADMIN_REVOKE_MEMBERSHIP_MUTATION = gql`
  mutation AdminRevokeCoachMembership($input: AdminRevokeCoachMembershipInput!) {
    adminRevokeCoachMembership(input: $input) {
      subscription {
        id
      }
      entitlement {
        isActive
      }
    }
  }
`;

const defaultExpiry = (): string => {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60 * 1000).toISOString().slice(0, 16);
};

const formatDate = (value?: string): string => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const statusVariant = (status: SubscriptionStatus): string => {
  if (status === 'ACTIVE') return 'success';
  if (status === 'GRACE_PERIOD' || status === 'CANCELED') return 'warning';
  if (status === 'PENDING' || status === 'PAUSED') return 'secondary';
  return 'danger';
};

export const AdminSubscriptions: React.FC = () => {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<SubscriptionStatus | ''>('');
  const [showGrant, setShowGrant] = useState(false);
  const [email, setEmail] = useState('');
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('YEARLY');
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState(defaultExpiry);
  const [note, setNote] = useState('');
  const [message, setMessage] = useState<string>();
  const [actionError, setActionError] = useState<string>();

  const variables = useMemo<AdminSubscriptionsVariables>(
    () => ({
      query: search.trim() || undefined,
      status: status || undefined,
      limit: 300,
    }),
    [search, status],
  );
  const { data, loading, error, refetch } = useQuery<
    AdminSubscriptionsData,
    AdminSubscriptionsVariables
  >(ADMIN_SUBSCRIPTIONS_QUERY, {
    variables,
    fetchPolicy: 'network-only',
  });
  const [grantMembership, { loading: granting }] = useMutation<
    { adminGrantCoachMembership: MutationResultData },
    {
      input: {
        email: string;
        billingPeriod: BillingPeriod;
        currentPeriodEnd: string;
        environment: 'SANDBOX';
        note?: string;
      };
    }
  >(ADMIN_GRANT_MEMBERSHIP_MUTATION);
  const [revokeMembership, { loading: revoking }] = useMutation<
    { adminRevokeCoachMembership: MutationResultData },
    { input: { subscriptionId: number; reason?: string } }
  >(ADMIN_REVOKE_MEMBERSHIP_MUTATION);

  const handleGrant = async () => {
    setActionError(undefined);
    setMessage(undefined);
    try {
      await grantMembership({
        variables: {
          input: {
            email: email.trim(),
            billingPeriod,
            currentPeriodEnd: new Date(currentPeriodEnd).toISOString(),
            environment: 'SANDBOX',
            note: note.trim() || undefined,
          },
        },
      });
      setShowGrant(false);
      setSearch(email.trim());
      setEmail('');
      setBillingPeriod('YEARLY');
      setCurrentPeriodEnd(defaultExpiry());
      setNote('');
      await refetch();
      setMessage('Coach Membership granted successfully.');
    } catch (grantError: unknown) {
      setActionError(grantError instanceof Error ? grantError.message : String(grantError));
    }
  };

  const handleRevoke = async (row: AdminSubscriptionRow) => {
    const confirmed = window.confirm(
      `Revoke subscription #${row.subscription.id} for ${row.user.email}?`,
    );
    if (!confirmed) return;

    setActionError(undefined);
    setMessage(undefined);
    try {
      await revokeMembership({
        variables: {
          input: {
            subscriptionId: row.subscription.id,
            reason: 'Revoked from the admin subscriptions page',
          },
        },
      });
      await refetch();
      setMessage(`Subscription #${row.subscription.id} revoked.`);
    } catch (revokeError: unknown) {
      setActionError(revokeError instanceof Error ? revokeError.message : String(revokeError));
    }
  };

  const rows = data?.adminCoachMembershipSubscriptions ?? [];

  return (
    <div className="w-100">
      <PageHeader
        title="Coach Memberships"
        subtitle={`${rows.length} subscription${rows.length === 1 ? '' : 's'}`}
        rightItem={(
          <Button variant="primary" onClick={() => setShowGrant(true)}>
            Grant membership
          </Button>
        )}
      />

      {error && <Alert variant="danger">{error.message}</Alert>}
      {actionError && <Alert variant="danger">{actionError}</Alert>}
      {message && <Alert variant="success">{message}</Alert>}

      <Card className="mb-4 w-100">
        <Card.Body>
          <Row>
            <Col md={8}>
              <Form.Group className="mb-0">
                <Form.Label>User</Form.Label>
                <Form.Control
                  value={search}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setSearch(event.target.value)
                  }
                  placeholder="Search by email or name"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-0">
                <Form.Label>Status</Form.Label>
                <Form.Control
                  as="select"
                  value={status}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setStatus(event.target.value as SubscriptionStatus | '')
                  }
                >
                  <option value="">All statuses</option>
                  <option value="ACTIVE">Active</option>
                  <option value="GRACE_PERIOD">Grace period</option>
                  <option value="PENDING">Pending</option>
                  <option value="CANCELED">Canceled</option>
                  <option value="EXPIRED">Expired</option>
                  <option value="REVOKED">Revoked</option>
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="w-100">
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead>
              <tr>
                <th>User</th>
                <th>Provider</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Entitlement</th>
                <th>Period end</th>
                <th>Auto renew</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="text-center py-4">Loading subscriptions...</td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">
                    No subscriptions found.
                  </td>
                </tr>
              )}
              {!loading && rows.map((row) => {
                const isSource = row.entitlement.subscription?.id === row.subscription.id;
                return (
                  <tr key={row.subscription.id}>
                    <td>
                      <strong>{row.user.fullName}</strong>
                      <div className="small text-muted">{row.user.email}</div>
                    </td>
                    <td>
                      {row.subscription.provider}
                      <div className="small text-muted">{row.subscription.environment}</div>
                    </td>
                    <td>{row.subscription.billingPeriod.toLowerCase()}</td>
                    <td>
                      <Badge variant={statusVariant(row.subscription.status)}>
                        {row.subscription.status}
                      </Badge>
                    </td>
                    <td>
                      <Badge variant={row.entitlement.isActive && isSource ? 'success' : 'secondary'}>
                        {row.entitlement.isActive && isSource ? 'ACTIVE' : 'INACTIVE'}
                      </Badge>
                    </td>
                    <td>{formatDate(row.subscription.currentPeriodEnd)}</td>
                    <td>{row.subscription.autoRenewing ? 'Yes' : 'No'}</td>
                    <td className="text-right">
                      <Button
                        size="sm"
                        variant="outline-danger"
                        disabled={revoking || row.subscription.status === 'REVOKED'}
                        onClick={() => handleRevoke(row)}
                      >
                        Revoke
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={showGrant} onHide={() => setShowGrant(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Grant Coach Membership</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Coach email</Form.Label>
            <Form.Control
              type="email"
              value={email}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setEmail(event.target.value)
              }
              placeholder="coach@example.com"
              autoFocus
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Billing period</Form.Label>
            <Form.Control
              as="select"
              value={billingPeriod}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setBillingPeriod(event.target.value as BillingPeriod)
              }
            >
              <option value="MONTHLY">Monthly</option>
              <option value="YEARLY">Yearly</option>
            </Form.Control>
          </Form.Group>
          <Form.Group>
            <Form.Label>Access valid until</Form.Label>
            <Form.Control
              type="datetime-local"
              value={currentPeriodEnd}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setCurrentPeriodEnd(event.target.value)
              }
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Internal note</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={note}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setNote(event.target.value)
              }
              placeholder="Optional reason or context"
            />
          </Form.Group>
          <Alert variant="secondary" className="mb-0">
            This creates a manual sandbox subscription and activates the same
            entitlement used by Apple, Google, and Stripe subscriptions.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowGrant(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={granting || !email.trim() || !currentPeriodEnd}
            onClick={handleGrant}
          >
            {granting ? 'Granting...' : 'Grant membership'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
